"use client"

import { useQuery } from "@tanstack/react-query"
import { createClient } from "@/shared/lib/supabase/client"
import { dbGalleryPostToGalleryPost } from "@/shared/lib/supabase/mappers"
import { dbCharacterToCharacter } from "@/shared/lib/supabase/mappers"
import { queryKeys } from "@/shared/lib/query-keys"
import {
  applyCharacterVisibility,
  profileCharactersQueryKey,
} from "@/features/profile/character-visibility"
import { getPublicProfile } from "@/features/profile/actions"
import type { PublicProfile } from "@/features/profile/actions"
import type { GalleryPost } from "@/shared/lib/supabase/mappers"
import type { Character } from "@/shared/types/character"

export function usePublicProfile(username: string) {
  return useQuery({
    queryKey: queryKeys.profileByUsername(username),
    queryFn: async (): Promise<PublicProfile | null> => {
      const result = await getPublicProfile(username)
      if (!result.success || !result.profile) return null
      return result.profile
    },
    enabled: !!username,
  })
}

export function useUserGalleryPosts(userId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.gallery.userPosts(userId ?? ""),
    queryFn: async (): Promise<GalleryPost[]> => {
      const supabase = createClient()

      const { data, error } = await supabase
        .from("gallery_posts")
        .select("*, profiles(username, avatar_url)")
        .eq("user_id", userId!)
        .eq("status", "active")
        .order("created_at", { ascending: false })

      if (error) throw error

      return (data ?? []).map(
        (row: Parameters<typeof dbGalleryPostToGalleryPost>[0]) =>
          dbGalleryPostToGalleryPost(row)
      )
    },
    enabled: !!userId,
  })
}

/**
 * Characters shown on a profile's Characters tab.
 *
 * Visitors only ever see the creator's public characters. When the viewer is
 * the profile owner (`includePrivate`), their own private characters are
 * listed too; RLS still restricts private rows to their creator, so this flag
 * cannot expose someone else's private characters. The two views use separate
 * cache keys so an owner-view list is never served to a visitor view.
 */
export function useUserCharacters(
  userId: string | undefined,
  options: { includePrivate?: boolean } = {}
) {
  const includePrivate = options.includePrivate === true
  return useQuery({
    queryKey: profileCharactersQueryKey(
      queryKeys.characters.byUser(userId ?? ""),
      includePrivate
    ),
    queryFn: async (): Promise<Character[]> => {
      const supabase = createClient()

      const query = supabase
        .from("characters")
        .select("*")
        .eq("created_by", userId!)
        .eq("status", "active")

      const { data, error } = await applyCharacterVisibility(query, includePrivate)
        .order("created_at", { ascending: false })

      if (error) throw error
      return (data ?? []).map(dbCharacterToCharacter)
    },
    enabled: !!userId,
  })
}

export function useUserLikedPosts(userId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.gallery.userLikes(userId ?? ""),
    queryFn: async (): Promise<GalleryPost[]> => {
      const supabase = createClient()

      // Fetch liked post IDs first, then fetch the posts
      const { data: likes, error: likesError } = await supabase
        .from("gallery_likes")
        .select("post_id")
        .eq("user_id", userId!)
        .order("created_at", { ascending: false })

      if (likesError) throw likesError
      if (!likes || likes.length === 0) return []

      const postIds = likes.map((l: { post_id: string }) => l.post_id)

      const { data: posts, error: postsError } = await supabase
        .from("gallery_posts")
        .select("*, profiles(username, avatar_url)")
        .in("id", postIds)
        .eq("status", "active")

      if (postsError) throw postsError

      // Preserve the liked order
      const postMap = new Map(
        (posts ?? []).map((p: { id: string }) => [p.id, p])
      )
      return postIds
        .map((id: string) => postMap.get(id))
        .filter(Boolean)
        .map((row) =>
          dbGalleryPostToGalleryPost(
            row as Parameters<typeof dbGalleryPostToGalleryPost>[0]
          )
        )
    },
    enabled: !!userId,
  })
}
